import { Temporal } from 'temporal-polyfill';

export const CALENDAR_TIMEZONE = 'Europe/Moscow';

export const tabs = [
  { key: 'calendar', label: 'Календарь' },
  { key: 'posts', label: 'Посты' },
  { key: 'clients', label: 'Клиенты' },
];

export const calendarViews = [
  { key: 'day', label: 'День' },
  { key: 'week', label: 'Неделя' },
  { key: 'month', label: 'Месяц' },
];

export const stateLabels = {
  scheduled: 'Запланировано',
  leased: 'Подготовка',
  sending: 'Отправка',
  accepted: 'Принято Telegram',
  observed: 'Подтверждено в группе',
  failed: 'Ошибка',
  unknown: 'Требует проверки',
  cancelled: 'Отменено',
};

const stateColors = {
  scheduled: ['#2563eb', '#ffffff'],
  leased: ['#475569', '#ffffff'],
  sending: ['#7c3aed', '#ffffff'],
  accepted: ['#15803d', '#ffffff'],
  observed: ['#15803d', '#ffffff'],
  failed: ['#dc2626', '#ffffff'],
  unknown: ['#b45309', '#ffffff'],
  cancelled: ['#64748b', '#ffffff'],
};

export const uuid = () => crypto.randomUUID();

function instant(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.valueOf())) throw new RangeError('Invalid date');
  return Temporal.Instant.fromEpochMilliseconds(date.valueOf());
}

export function calendarDate(value, timezone = CALENDAR_TIMEZONE) {
  return instant(value).toZonedDateTimeISO(timezone).toPlainDate().toString();
}

export function calendarDateTime(value, timezone = CALENDAR_TIMEZONE) {
  return instant(value)
    .toZonedDateTimeISO(timezone)
    .toPlainDateTime()
    .toString({ smallestUnit: 'minute' });
}

export function calendarDateTimeToIso(value, timezone = CALENDAR_TIMEZONE) {
  return Temporal.PlainDateTime.from(value)
    .toZonedDateTime(timezone)
    .toInstant()
    .toString();
}

export function normalizeCalendarView(value) {
  return calendarViews.some(item => item.key === value) ? value : 'month';
}

export function normalizeCalendarDate(
  value,
  fallback = new Date(),
  timezone = CALENDAR_TIMEZONE
) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value || '')
    ? value
    : calendarDate(fallback, timezone);
}

export function normalizeGroupId(value) {
  return /^[1-9]\d{0,18}$/.test(String(value || '')) ? String(value) : '';
}

export function normalizePostSource(value) {
  return /^[1-9]\d{0,18}$/.test(String(value || '')) ? String(value) : '';
}

export function calendarRangeSignature(range, groupId = '') {
  if (!range?.start || !range?.end) return '';
  return [
    String(groupId || ''),
    range.start,
    range.end,
    normalizeCalendarView(range.view),
  ].join('|');
}

export function publicationEvent(run, sourceName = '', source = {}) {
  const [color, contrastColor] = stateColors[run.state] || stateColors.unknown;
  const title = run.post_title || run.text_preview || 'Публикация';
  return {
    id: String(run.id),
    title,
    start: run.run_at,
    color,
    contrastColor,
    extendedProps: {
      run,
      sourceName,
      sourceUsername: source?.username || '',
      sourceAvatar: source?.avatar_url || '',
      status: stateLabels[run.state] || 'Неизвестный статус',
    },
  };
}

export function errorText(error) {
  const code = error?.response?.data?.error;
  const messages = {
    fill_settings_required:
      'Сначала сохраните настройки заполнения для этой группы.',
    group_timezone_required:
      'Город и часовой пояс группы не определены. Планирование недоступно.',
    group_timezone_mismatch:
      'Часовой пояс группы изменился. Обновите календарь и проверьте местное время.',
    invalid_fill_mode: 'Выберите режим заполнения.',
    invalid_daily_range:
      'Укажите количество публикаций от 1 до 12: минимум не больше максимума.',
    invalid_time_window:
      'Укажите окно от 10:00 до 21:30, вмещающее максимум публикаций с интервалом час.',
    invalid_sources: 'Выберите от 1 до 100 подключённых аккаунтов.',
    invalid_daily_times:
      'Укажите часы от 10:00 до 21:30 через запятую, с интервалом минимум час.',
    invalid_weekdays: 'Выберите хотя бы один день недели.',
    fill_history_too_large:
      'В группе слишком много заданий для одного заполнения. Сократите период.',
    fill_library_too_large:
      'Выбрано больше 2000 постов. Сократите список аккаунтов.',

    membership_check_required:
      'Аккаунт ещё проверяет право писать в эту группу. Обновите данные через несколько секунд.',
    client_limit_reached: 'Все оплаченные публикации клиента уже заняты.',
    run_at_too_soon: 'Выберите время минимум через 30 секунд.',
    run_at_too_far: 'Нельзя планировать дальше чем на два года.',
    revision_conflict: 'Запись уже изменилась. Обновите страницу.',
    request_key_conflict: 'Результат операции неоднозначен. Обновите данные.',
    invalid_media: 'Проверьте формат вложений.',
    media_too_large: 'Общий размер вложений превышает 50 МБ.',
    plan_expired: 'Время предпросмотра истекло. Создайте план заново.',
    plan_stale:
      'Пост, лимит или календарь изменился. Создайте новый предпросмотр.',
    no_available_slots: 'В выбранном периоде нет свободных слотов.',
    plan_too_large: 'За один раз можно запланировать не более 500 публикаций.',
  };
  if (messages[code]) return messages[code];
  if ([401, 403].includes(error?.response?.status))
    return 'Нет доступа. Раздел доступен администратору.';
  if (error?.response?.status === 404)
    return 'Раздел пока не включён или запись уже удалена.';
  return 'Не удалось выполнить действие. Повторите попытку.';
}
